import { useEffect, useRef } from "react";
import io, { Socket } from "socket.io-client";

const SOCKET_BASE_URL = process.env.REACT_APP_SOCKET_BASE_URL || "http://localhost:8095";

export const ITEM_LOCATIONS_SEED_COMPLETED_EVENT = "item_locations_seed_completed";

/**
 * A Board Settings "Add column" click has finished: every orchestration chunk job that click
 * created is terminal. Emitted by ms-utils into the `user:<id>` room on behalf of
 * simb-orchestration's item-locations seed notifier.
 */
export interface ItemLocationsSeedCompletedEvent {
  userId: string;
  accountId?: string;
  requestId: string;
  boardId?: string;
  summary: { totalChunks: number; done: number; dead: number; syncGroups: number; boards: number };
  reason: "no_sync_group" | null;
  message: string;
}

interface UseItemLocationsSeedStreamArgs {
  userId: string | number | undefined;
  accountId?: string | number;
  onCompleted: (event: ItemLocationsSeedCompletedEvent) => void;
  enabled?: boolean;
}

// Subscribes to the ms-utils notification socket and forwards item-locations seed completions
// addressed to this user. Mirrors useForcePushStream.
const useItemLocationsSeedStream = ({
  userId,
  accountId,
  onCompleted,
  enabled = true,
}: UseItemLocationsSeedStreamArgs) => {
  // Keep the latest callback in a ref so the socket effect does not reconnect on every render.
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;

  useEffect(() => {
    if (!enabled || !userId) return;

    const url = `${SOCKET_BASE_URL}/?userid=${userId}&accountId=${accountId ?? ""}`;
    const socket: Socket = io(url);

    // The server only emits to this user's room; the guard is defense in depth against a
    // misrouted emit settling another user's click.
    const isForThisUser = (incomingUserId: string | number | undefined): boolean =>
      String(incomingUserId) === String(userId);

    socket.on(ITEM_LOCATIONS_SEED_COMPLETED_EVENT, (data: ItemLocationsSeedCompletedEvent) => {
      if (isForThisUser(data?.userId)) onCompletedRef.current(data);
    });

    return () => {
      socket.disconnect();
    };
  }, [userId, accountId, enabled]);
};

export default useItemLocationsSeedStream;
